import { useCallback } from 'react';

import { useSelector } from '@xstate5/react';
import { type ActorRefFrom } from 'xstate5';

import { type otaMachine } from './ota-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook to provide simplified access to the OTA machine via the provided reference.
 */
export const useOtaMachine = (params: UseOtaMachineParams) => {
  const { actorRef } = params;

  // ─── State ───────────────────────────────────────────────────────────

  const hasPendingUpdate = useSelector(actorRef, (snapshot) => snapshot.matches('has-update'));

  // ─── Helpers ─────────────────────────────────────────────────────────

  const applyUpdate = useCallback(() => {
    if (!hasPendingUpdate) return;

    actorRef.send({ type: 'update' });
  }, [actorRef, hasPendingUpdate]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    actorRef,
    state: { hasPendingUpdate },
    helpers: { applyUpdate },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOtaMachineParams = {
  actorRef: ActorRefFrom<typeof otaMachine>;
};
